import { landmineNames } from '@mm/constants/landmine-names';
import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import { PageBillingType } from '@mm/state/interfaces/connected-page';
import ButtonModel, {
  ButtonType,
  WebviewOptions,
} from '@mm/state/model/ButtonModel';
import { PageKind } from '@mm/state/model/PageModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import { activeFacebookPagePlanInfoSelector } from '@mm/state/selectors/facebookPageSelector';
import { pageEntityByIdSelector } from '@mm/state/selectors/pageSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useSafeTimeout } from '@mm/hooks';
import { ReactComponent as TopWidgetArrow } from 'assets/top-widget-arrow.svg';
import classnames from 'classnames';
import { useFormik } from 'formik';
import moment from 'moment';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import AttributeContainingTextInput from '../../../../editor/components/AttributeContainingTextInput/AttributeContainingTextInput';
import { ButtonTagsForm } from '../../../../editor/features/build/components/widgets/buttons/ButtonConfigform/ButtonTagsForm';
import { MAX_BUTTON_LENGTH } from '../../../../editor/features/configure/components/InputBase';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../buttons/PrimaryButton/PrimaryButton';
import { getUpdatedTextWithAttribute } from '../../../components/AttributePicker/attribute-picker-helpers';
import AttributePicker from '../../../components/AttributePicker/AttributePicker';
import { getUpdatedTextWithEmoji } from '../../../components/EmojiPicker/emoji-picker-helpers';
import { EmojiPicker } from '../../../components/EmojiPicker/EmojiPicker';
import { generatePageStructure } from '../../../components/FilterableSelect/filterable-select-helpers';
import FormFieldWrapper from '../../../components/FormFieldWrapper/FormFieldWrapper';
import { PhoneInput } from '../../../components/PhoneInput/PhoneInput';
import PrimarySelect from '../../../components/PrimarySelect/PrimarySelect';
import TextInput from '../../../components/TextInput';
import { TooltipContainer } from '../../../components/TooltipContainer/TooltipContainer';
import { WrappedTextInput } from '../../../components/WrappedTextInput/WrappedTextInput';
import { useIsOnboardingRouteCheck } from '../../../hooks/use-is-onboarding-route-check';
import { PathGraphContext } from '../../common/PathGraph/PathGraph';
import { useInputRef } from '../../common/use-input-ref';
import {
  TextWidgetButtonDefinition,
  useTextWidgetButtonsList,
} from './buttons-list';
import FormHelpers, {
  CreateEditButtonFormValues,
} from './create-edit-button-form-helpers';
import './CreateEditButtonArea.pcss';
import { useSubmitForm } from './use-submit-form';

type Props = {
  chatbotId: number;
  widget: WidgetModel;
  button?: ButtonModel;
  forAds?: boolean;
  closeButtonArea: () => any;
  isOnboardingRoute?: boolean;
  disabledButtonTypes?: ButtonType[];
  usedInSlideout?: boolean;
};

export const useDialogueDropdownState = (pageId: number | undefined) => {
  const pages = useSelector((state: StoreState) => state.page.entities);
  const selectedPage = pages.find(p => p.id === pageId);
  const viewBtnDisabled =
    !selectedPage || selectedPage.kind === PageKind.UnsubscribePage;
  const pageGroups = useSelector(
    (state: StoreState) => state.pageGroup.entities,
  );
  const dialogueDropdownOptions = generatePageStructure(pages, pageGroups);

  return {
    dialogueDropdownOptions,
    selectedPage,
    viewBtnDisabled,
  };
};

const TAGS_TEXT = 'Add a Tag to contacts who click this button';

const getSelectedButton = (
  buttonsList: TextWidgetButtonDefinition[],
  selectedButtonType: ButtonType,
) => {
  return buttonsList.find(b => b.type === selectedButtonType) || buttonsList[0];
};

export const CreateEditButtonArea = (props: Props) => {
  const {
    button,
    widget,
    closeButtonArea,
    forAds,
    chatbotId,
    disabledButtonTypes,
    usedInSlideout,
  } = props;
  const { safeTimeout } = useSafeTimeout();
  const dispatch: Dispatch<StoreState> = useDispatch();
  const { inputRef, memoizedSetInputRef } = useInputRef(true);
  const { inputRef: textInputRef, memoizedSetInputRef: setTextInputRef } =
    useInputRef(true);

  const { pathGraph } = useContext(PathGraphContext);

  const topLevelDialogue = useSelector((state: StoreState) =>
    pageEntityByIdSelector(state, pathGraph?.graph() || ''),
  );

  const pageCRUDActions = usePageCRUDActions();

  const { isOnboardingRoute } = useIsOnboardingRouteCheck();
  const buttonsList = useTextWidgetButtonsList({
    forAds,
    isOnboardingRoute,
    disabledButtonTypes,
  });

  const formProps = useFormik<CreateEditButtonFormValues>({
    initialValues: FormHelpers.getInitialValues(button),
    validate: FormHelpers.validate(true),
    onSubmit: values => submitForm(values, button),
  });

  const getNewDialogueName = () => {
    return `${
      formProps.values.buttonName === 'buttonName' ||
      formProps.values.buttonName === ''
        ? moment().format('YYYY-MM-DD HH:mm')
        : formProps.values.buttonName
    }`;
  };

  const [newDialogueName, setNewDialogueName] = useState(getNewDialogueName());

  useEffect(() => {
    setNewDialogueName(getNewDialogueName());
  }, [formProps.values.buttonName]);

  useEffect(() => {
    if (selectedButton) {
      formProps.setFieldValue(
        FormHelpers.formFields.selectedButtonType,
        selectedButton.type,
      );
    }
  }, []);

  const submitForm = useSubmitForm(
    widget,
    chatbotId,
    closeButtonArea,
    newDialogueName,
  );
  const freeOrSpecialPricePlan = useSelector(
    (state: StoreState) =>
      activeFacebookPagePlanInfoSelector(state).info.isFreeOrSpecialPlan,
  );

  const selectedButton = getSelectedButton(
    buttonsList,
    formProps.values.selectedButtonType,
  );

  const { dialogueDropdownOptions, selectedPage, viewBtnDisabled } =
    useDialogueDropdownState(Number(formProps.values.pageId));

  const updateInsertEmojiQuestion = (emoji: string) => {
    if (inputRef) {
      const newText = getUpdatedTextWithEmoji(
        formProps.values.buttonName,
        emoji,
        inputRef,
      );
      formProps.setFieldValue(FormHelpers.formFields.buttonName, newText);
    }
  };

  const onAttributePicked = (attribute: string) => {
    const {
      values: { url },
    } = formProps;
    const newText = getUpdatedTextWithAttribute(url, attribute, textInputRef);
    formProps.setFieldValue(FormHelpers.formFields.url, newText);
    formProps.setFieldTouched(FormHelpers.formFields.url);

    safeTimeout(() => {
      if (textInputRef) {
        textInputRef.focus();
      }
    }, 100);
  };

  const onClickButtonType = (buttonType: ButtonType) => {
    if (buttonType === ButtonType.HANDOVER && freeOrSpecialPricePlan) {
      dispatch(
        modalActions.openUniversalUpgradeLandmineModal({
          landmineName: landmineNames['WIDGETS__TEXT__HANDOVER-BUTTON'],
          planFallback: PageBillingType.Pro,
        }),
      );
    } else {
      formProps.setFieldValue(
        FormHelpers.formFields.selectedButtonType,
        buttonType,
      );
      formProps.setFieldTouched(FormHelpers.formFields.buttonName, false);
      formProps.validateForm();
    }
  };

  const onTagAdd = (tag: { name: string }) => {
    formProps.setFieldValue(FormHelpers.formFields.tags, [
      ...formProps.values.tags,
      tag,
    ]);
  };

  const onTagRemove = (tag: { name: string }) => {
    formProps.setFieldValue(
      FormHelpers.formFields.tags,
      formProps.values.tags.filter(e => e.name !== tag.name),
    );
  };

  const [showSearchRightSide, setShowSearchRightSide] = useState(true);
  const [showUnderSearchInsert, setShowUnderSearchInsert] = useState(false);

  const searchRightSide = useMemo(
    () => (
      <div
        className="create-edit-button-area__associated-dialogue-input--new-dialogue"
        onClick={() => {
          setShowUnderSearchInsert(true);
          setShowSearchRightSide(false);
        }}
      >
        {showSearchRightSide && '+ New Dialogue'}
      </div>
    ),
    [showSearchRightSide, setShowSearchRightSide, setShowUnderSearchInsert],
  );

  const createNewDialogue = async () => {
    setShowUnderSearchInsert(false);
    const response = await pageCRUDActions.create({
      botId: chatbotId,
      name: newDialogueName,
      kind: PageKind.StartBot,
      pageGroupId: topLevelDialogue?.pageGroupId,
    });

    if (response && response.success) {
      const { page } = response;
      formProps.setFieldValue(FormHelpers.formFields.pageId, page.id);
    }
  };

  const underSearchInsert = useMemo(
    () =>
      showUnderSearchInsert ? (
        <div className="create-edit-button-area__new-dialogue-container">
          <TextInput
            className="create-edit-button-area__new-dialogue-input"
            value={newDialogueName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setNewDialogueName(e.target.value)
            }
            placeholder="Name New Dialogue"
            autoFocus
          />
          <PrimaryButton
            className="create-edit-button-area__new-dialogue-button"
            onClick={createNewDialogue}
            size="lg"
          >
            Create
          </PrimaryButton>
        </div>
      ) : undefined,
    [
      showUnderSearchInsert,
      newDialogueName,
      setNewDialogueName,
      createNewDialogue,
    ],
  );

  const userCountry = useSelector((store: StoreState) => store.env.userCountry);

  return (
    <form onSubmit={formProps.handleSubmit} className="create-edit-button-area">
      <div className="create-edit-button-area__text-wrapper">
        <WrappedTextInput
          fieldName={FormHelpers.formFields.buttonName}
          formProps={formProps}
          maxLength={MAX_BUTTON_LENGTH}
          inputRef={memoizedSetInputRef}
          className="create-edit-button-area__name-input"
          placeholder="Button name"
        />

        <EmojiPicker
          onEmojiPicked={updateInsertEmojiQuestion}
          className={classnames('create-edit-button-area__emoji-picker', {
            'create-edit-button-area--emoji-picker-disabled':
              formProps.values.buttonName.length >= MAX_BUTTON_LENGTH,
          })}
        />
      </div>

      <div className="create-edit-button-area__button-type-options">
        {buttonsList.map(
          ({ type, iconClassList, text, iconComponent: IconComponent }) => (
            <div
              key={type}
              onClick={() => onClickButtonType(type)}
              className={classnames('create-edit-button-area__button-type', {
                'create-edit-button-area__button-type--selected':
                  selectedButton?.type === type,
              })}
            >
              {iconClassList && (
                <i
                  className={classnames(
                    'create-edit-button-area__button-icon',
                    iconClassList,
                  )}
                />
              )}

              {IconComponent && (
                <IconComponent className="create-edit-button-area__button-icon create-edit-button-area__button-icon--component" />
              )}
              <span>{text}</span>
            </div>
          ),
        )}
      </div>

      <div className="create-edit-button-area__custom-fields-by-type">
        {selectedButton?.type === ButtonType.CALL && (
          <FormFieldWrapper
            className="create-edit-button-area__url-fields-wrapper"
            error={formProps.errors.phoneNumber}
            touched={formProps.touched.phoneNumber}
          >
            <PhoneInput
              name={FormHelpers.formFields.phoneNumber}
              className="create-edit-button-area__phone-input"
              value={formProps.values.phoneNumber}
              country={userCountry}
              onChange={value =>
                formProps.setFieldValue(
                  FormHelpers.formFields.phoneNumber,
                  value,
                )
              }
              onBlur={() =>
                formProps.setFieldTouched(FormHelpers.formFields.phoneNumber)
              }
              errorState={
                formProps.touched.phoneNumber && !!formProps.errors.phoneNumber
              }
            />
          </FormFieldWrapper>
        )}

        {selectedButton?.type === ButtonType.URL && (
          <>
            <div className="create-edit-button-area__field">
              <div className="create-edit-button-area__field-label">
                Website Address
              </div>
              <FormFieldWrapper
                className="create-edit-button-area__url-fields-wrapper"
                error={formProps.errors.url}
                touched={formProps.touched.url}
              >
                <AttributeContainingTextInput
                  inputRef={setTextInputRef}
                  value={formProps.values.url}
                  onChange={e =>
                    formProps.setFieldValue(
                      FormHelpers.formFields.url,
                      e.target.value,
                    )
                  }
                  onBlur={() =>
                    formProps.setFieldTouched(FormHelpers.formFields.url)
                  }
                  useStoreAttributes
                />
                <AttributePicker
                  className="button-config__input-url-field-attribute-picker"
                  onAttributePicked={onAttributePicked}
                />
              </FormFieldWrapper>
            </div>
            <div className="create-edit-button-area__field">
              <div className="create-edit-button-area__field-label">
                <div className="create-edit-button-area__field-label-text">
                  Webview Size
                </div>
                <TooltipContainer
                  classNameContent="create-edit-button-area__webview-size-tooltip"
                  classNameAnchor="create-edit-button-area__webview-size-anchor"
                  message={
                    <a
                      href="https://mobilemonkey.com/help/article/ibx5q9zk7i-webview-size"
                      target="_blank"
                      className="create-edit-button-area__webview-size-link"
                    >
                      About Webview Size
                    </a>
                  }
                />
              </div>
              <PrimarySelect
                showSearchFilter={false}
                placeholder="Select Webview Size"
                className="create-edit-button-area__select-webview"
                onChange={webviewSize =>
                  formProps.setFieldValue(
                    FormHelpers.formFields.webviewSize,
                    webviewSize.id,
                  )
                }
                options={WebviewOptions}
                value={WebviewOptions.find(
                  ({ id }) => id === formProps.values.webviewSize,
                )}
                customDropdownStyles={{
                  zIndex: usedInSlideout ? '1000003' : '999',
                }}
              />
            </div>
          </>
        )}

        {selectedButton?.type === ButtonType.HANDOVER && (
          <div className="create-edit-button-area__handover-fields-wrapper">
            <div className="create-edit-button-area__handover-text">
              Developer Feature: Pass this conversation to another Messenger
              application using&nbsp;
              <a
                href="https://developers.facebook.com/docs/messenger-platform/handover-protocol/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Facebook&#39;s Handover Protocol
              </a>
              .
            </div>

            <WrappedTextInput
              type="number"
              fieldName={FormHelpers.formFields.appId}
              formProps={formProps}
              placeholder="Enter App Id"
            />

            <WrappedTextInput
              type="text"
              fieldName={FormHelpers.formFields.metadata}
              formProps={formProps}
              placeholder="Enter metadata"
            />
          </div>
        )}

        {selectedButton?.type === ButtonType.PAGE && (
          <div className="create-edit-button-area__associated-dialogue">
            <div className="create-edit-button-area__associated-dialogue-text">
              Take users to a new Dialogue?
            </div>

            <div className="create-edit-button-area__associated-dialogue-input-wrapper">
              <PrimarySelect
                showSearchFilter={true}
                placeholder="Select Dialogue"
                className="create-edit-button-area__associated-dialogue-input"
                onChange={dialogue =>
                  formProps.setFieldValue(
                    FormHelpers.formFields.pageId,
                    dialogue.id,
                  )
                }
                organizedOptions={dialogueDropdownOptions}
                value={selectedPage}
                searchRightSide={searchRightSide}
                underSearchInsert={underSearchInsert}
                customDropdownStyles={{
                  zIndex: usedInSlideout ? '1000003' : '999',
                }}
              />
            </div>
          </div>
        )}

        {[ButtonType.PAGE, ButtonType.URL].includes(selectedButton?.type) && (
          <div className="create-edit-button-area__tags-wrapper">
            <ButtonTagsForm
              title={TAGS_TEXT}
              kind="add_tags"
              tags={formProps.values.tags}
              onTagAdd={onTagAdd}
              onTagRemove={onTagRemove}
            />
          </div>
        )}
      </div>

      <div className="create-edit-button-area__bottom-area">
        <div className="create-edit-button-area__bottom-buttons">
          <PrimaryButton
            onClick={closeButtonArea}
            color={ButtonColor.secondaryBlue}
          >
            Cancel
          </PrimaryButton>

          <PrimaryButton
            disabled={!formProps.isValid || !selectedButton}
            className="create-edit-button-area__save-button"
            onClick={formProps.submitForm}
          >
            {button ? 'Save' : 'Create'}
          </PrimaryButton>
        </div>
      </div>

      <TopWidgetArrow className="create-edit-button-area__bottom-arrow" />
    </form>
  );
};
